"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { getDisplayQuickVoteAmounts, getQuickVoteAbsoluteRemainingPower, getQuickVoteRatingRange, isMemesQuickVoteVoteableDrop } from "@/hooks/memesQuickVote.helpers";
import { applyFailedDropRestoreState, applyFetchedWindowState, applyOptimisticAdvanceState, clampDropRatingRange, createInitialOptimisticRemainingVotePowerState, createInitialSessionState, getCurrentOptimisticRemainingVotePowerState, getEffectiveOptimisticRemainingVotePower, getUniqueDrops, reconcileOptimisticRemainingVotePower, reduceOptimisticRemainingVotePower, restoreOptimisticRemainingVotePower, type KeyedOptimisticRemainingVotePowerState, type MemesQuickVoteSessionState } from "@/hooks/memesQuickVote.queue.state";
import { fetchMemesQuickVoteUndiscoveredDrop, MEMES_QUICK_VOTE_LOOKAHEAD_COUNT, MEMES_QUICK_VOTE_UNDISCOVERED_DROP_QUERY_KEY } from "@/hooks/memesQuickVote.query";
import { useMemesQuickVoteContext } from "@/hooks/useMemesQuickVoteContext";
import { useMemesQuickVoteStorage } from "@/hooks/useMemesQuickVoteStorage";
import { useMemesQuickVoteSubmit } from "@/hooks/useMemesQuickVoteSubmit";
import { useQueryClient } from "@tanstack/react-query";
import { type ContextType, type Dispatch, type SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
type UseMemesQuickVoteQueueOptions = {
  readonly enabled?: boolean | undefined;
  readonly sessionId: number;
};
export type UseMemesQuickVoteQueueResult = {
  readonly activeDrop: ExtendedDrop | null;
  readonly hasDiscoveryError: boolean;
  readonly isExhausted: boolean;
  readonly isLoading: boolean;
  readonly isRestartingRound: boolean;
  readonly isReady: boolean;
  readonly leftThisRoundCount: number;
  readonly latestUsedAmount: number | null;
  readonly nextDrop: ExtendedDrop | null;
  readonly recentAmounts: number[];
  readonly retryDiscovery: () => void;
  readonly submitVote: (drop: ExtendedDrop, amount: number | string) => Promise<boolean>;
  readonly skipDrop: (drop: ExtendedDrop) => Promise<boolean>;
  readonly uncastPower: number | null;
  readonly unratedCount: number;
  readonly votingLabel: string | null;
};
type KeyedSessionState = {
  readonly key: string;
  readonly value: MemesQuickVoteSessionState;
};
type UseMemesQuickVoteSessionStateOptions = {
  readonly contextProfile: string | null | undefined;
  readonly enabled: boolean;
  readonly isQuickVoteEnabled: boolean;
  readonly isSettingsLoaded: boolean;
  readonly pendingDropIdsRef: {
    current: readonly string[];
  };
  readonly stateKey: string;
  readonly waveId: string | null;
};
type UseMemesQuickVoteSessionStateResult = {
  readonly advanceVisibleDrop: (drop: ExtendedDrop) => void;
  readonly restoreFailedDrop: (drop: ExtendedDrop) => void;
  readonly sessionState: MemesQuickVoteSessionState;
  readonly syncUndiscoveredWindow: () => Promise<void>;
};
type UseKeyedMemesQuickVoteSessionStoreResult = {
  readonly currentSessionState: MemesQuickVoteSessionState;
  readonly setCurrentSessionState: (nextState: MemesQuickVoteSessionState | ((current: MemesQuickVoteSessionState) => MemesQuickVoteSessionState)) => void;
};
type UseMemesQuickVoteWindowSyncOptions = Omit<UseMemesQuickVoteSessionStateOptions, "stateKey"> & {
  readonly setCurrentSessionState: UseKeyedMemesQuickVoteSessionStoreResult["setCurrentSessionState"];
};
const runBestEffortSync = (sync: () => Promise<void>): void => {
  sync().catch(() => {
    // Session state already models sync failures; callers should not block on this.
  });
};
const QUICK_VOTE_RESTART_TIMEOUT_MS = 3000;
const shouldRerunQuickVoteSync = ({
  abortController,
  rerunRequestedRef
}: {
  readonly abortController: AbortController;
  readonly rerunRequestedRef: {
    current: boolean;
  };
}): boolean => !abortController.signal.aborted && rerunRequestedRef.current;
const getQuickVoteSyncPendingState = (current: MemesQuickVoteSessionState): MemesQuickVoteSessionState => ({
  ...current,
  hasDiscoveryError: false,
  isExhausted: false,
  isRestartingRound: current.isRestartingRound,
  isLoading: current.currentDrop === null
});
const getQuickVoteSyncFailureState = (current: MemesQuickVoteSessionState): MemesQuickVoteSessionState => current.currentDrop ? {
  ...current,
  isRestartingRound: false,
  isLoading: false
} : {
  ...current,
  hasDiscoveryError: true,
  isExhausted: false,
  isRestartingRound: false,
  isLoading: false
};
const getCurrentSessionState = ({
  key,
  state
}: {
  readonly key: string;
  readonly state: KeyedSessionState;
}): MemesQuickVoteSessionState => state.key === key ? state.value : createInitialSessionState();
const useKeyedMemesQuickVoteSessionStore = stateKey => {
  const $ = _c(10);
  let t0;
  if ($[0] !== stateKey) {
    t0 = () => ({
      key: stateKey,
      value: createInitialSessionState()
    });
    $[0] = stateKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [sessionState, setSessionState] = useState(t0);
  let t1;
  if ($[2] !== stateKey) {
    t1 = nextState => {
      setSessionState(current => {
        const baseState = getCurrentSessionState({
          key: stateKey,
          state: current
        });
        const value = typeof nextState === "function" ? nextState(baseState) : nextState;
        return {
          key: stateKey,
          value
        };
      });
    };
    $[2] = stateKey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const setCurrentSessionState = t1;
  let t2;
  if ($[4] !== sessionState || $[5] !== stateKey) {
    t2 = getCurrentSessionState({
      key: stateKey,
      state: sessionState
    });
    $[4] = sessionState;
    $[5] = stateKey;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== setCurrentSessionState || $[8] !== t2) {
    t3 = {
      currentSessionState: t2,
      setCurrentSessionState
    };
    $[7] = setCurrentSessionState;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
const useMemesQuickVoteWindowSync = ({
  contextProfile,
  enabled,
  isQuickVoteEnabled,
  isSettingsLoaded,
  pendingDropIdsRef,
  setCurrentSessionState,
  waveId
}: UseMemesQuickVoteWindowSyncOptions) => {
  const syncAbortControllerRef = useRef<AbortController | null>(null);
  const syncInFlightRef = useRef<boolean>(false);
  const syncRequestIdRef = useRef(0);
  const syncRerunRequestedRef = useRef<boolean>(false);
  const syncRetryTimeoutRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const clearSyncRetryTimeout = useCallback(() => {
    if (syncRetryTimeoutRef.current === null) {
      return;
    }
    globalThis.clearTimeout(syncRetryTimeoutRef.current);
    syncRetryTimeoutRef.current = null;
  }, []);
  const stopActiveSync = useCallback(() => {
    syncRequestIdRef.current += 1;
    syncInFlightRef.current = false;
    syncRerunRequestedRef.current = false;
    syncAbortControllerRef.current?.abort();
    syncAbortControllerRef.current = null;
    clearSyncRetryTimeout();
  }, [clearSyncRetryTimeout]);
  const syncUndiscoveredWindow = useCallback(async function syncWindow() {
    if (syncInFlightRef.current) {
      syncRerunRequestedRef.current = true;
      return;
    }
    if (!enabled) {
      return;
    }
    if (isSettingsLoaded && !isQuickVoteEnabled) {
      setCurrentSessionState({
        ...createInitialSessionState(),
        isExhausted: true
      });
      return;
    }
    if (!isQuickVoteEnabled || !contextProfile || waveId === null) {
      return;
    }
    syncInFlightRef.current = true;
    syncRerunRequestedRef.current = false;
    const requestId = syncRequestIdRef.current + 1;
    syncRequestIdRef.current = requestId;
    const abortController = new AbortController();
    syncAbortControllerRef.current = abortController;
    setCurrentSessionState(getQuickVoteSyncPendingState);
    try {
      const responses = await Promise.all(Array.from({
        length: MEMES_QUICK_VOTE_LOOKAHEAD_COUNT
      }, (_, skip) => fetchMemesQuickVoteUndiscoveredDrop({
        signal: abortController.signal,
        skip,
        waveId
      })));
      if (abortController.signal.aborted || syncRequestIdRef.current !== requestId) {
        return;
      }
      const primaryResponse = responses[0] ?? null;
      const primaryDrop = primaryResponse?.drop ?? null;
      const rawLeftThisRoundCount = primaryResponse?.left_to_vote_in_current_round ?? 0;
      const rawUnratedCount = primaryResponse?.total_count ?? 0;
      const fetchedDrops = getUniqueDrops(responses.flatMap(response => response.drop ? [response.drop] : []));
      setCurrentSessionState(current => applyFetchedWindowState({
        current,
        fetchedDrops,
        pendingDropIds: pendingDropIdsRef.current,
        primaryDrop,
        rawLeftThisRoundCount,
        rawUnratedCount
      }));
    } catch {
      if (abortController.signal.aborted || syncRequestIdRef.current !== requestId) {
        return;
      }
      setCurrentSessionState(getQuickVoteSyncFailureState);
    } finally {
      if (syncAbortControllerRef.current === abortController) {
        syncAbortControllerRef.current = null;
      }
      syncInFlightRef.current = false;
      if (shouldRerunQuickVoteSync({
        abortController,
        rerunRequestedRef: syncRerunRequestedRef
      })) {
        syncRerunRequestedRef.current = false;
        runBestEffortSync(syncWindow);
      }
    }
  }, [contextProfile, enabled, isQuickVoteEnabled, isSettingsLoaded, pendingDropIdsRef, setCurrentSessionState, waveId]);
  return {
    clearSyncRetryTimeout,
    stopActiveSync,
    syncUndiscoveredWindow
  };
};
const useMemesQuickVoteSessionEffects = t0 => {
  const $ = _c(43);
  const {
    contextProfile,
    currentSessionState,
    enabled,
    isQuickVoteEnabled,
    pendingDropIdsRef,
    setCurrentSessionState,
    stateKey,
    stopActiveSync,
    syncUndiscoveredWindow,
    waveId
  } = t0;
  let t1;
  if ($[0] !== contextProfile || $[1] !== enabled || $[2] !== isQuickVoteEnabled || $[3] !== stopActiveSync || $[4] !== syncUndiscoveredWindow || $[5] !== waveId) {
    t1 = () => {
      stopActiveSync();
      if (!enabled || !isQuickVoteEnabled || !contextProfile || waveId === null) {
        return;
      }
      const timeoutId = globalThis.setTimeout(() => {
        runBestEffortSync(syncUndiscoveredWindow);
      }, 0);
      return () => {
        globalThis.clearTimeout(timeoutId);
        stopActiveSync();
      };
    };
    $[0] = contextProfile;
    $[1] = enabled;
    $[2] = isQuickVoteEnabled;
    $[3] = stopActiveSync;
    $[4] = syncUndiscoveredWindow;
    $[5] = waveId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== contextProfile || $[8] !== enabled || $[9] !== isQuickVoteEnabled || $[10] !== stateKey || $[11] !== stopActiveSync || $[12] !== syncUndiscoveredWindow || $[13] !== waveId) {
    t2 = [contextProfile, enabled, isQuickVoteEnabled, stateKey, stopActiveSync, syncUndiscoveredWindow, waveId];
    $[7] = contextProfile;
    $[8] = enabled;
    $[9] = isQuickVoteEnabled;
    $[10] = stateKey;
    $[11] = stopActiveSync;
    $[12] = syncUndiscoveredWindow;
    $[13] = waveId;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  useEffect(t1, t2);
  let t3;
  if ($[15] !== currentSessionState.currentDrop || $[16] !== currentSessionState.hasDiscoveryError || $[17] !== currentSessionState.isExhausted || $[18] !== currentSessionState.isRestartingRound || $[19] !== currentSessionState.recentlyHandledDropIds.length || $[20] !== enabled || $[21] !== pendingDropIdsRef || $[22] !== syncUndiscoveredWindow) {
    t3 = () => {
      if (!enabled || currentSessionState.hasDiscoveryError || currentSessionState.isExhausted) {
        return;
      }
      const shouldContinueSync = currentSessionState.isRestartingRound || currentSessionState.recentlyHandledDropIds.length > 0 || pendingDropIdsRef.current.length > 0 && currentSessionState.currentDrop === null;
      if (!shouldContinueSync) {
        return;
      }
      const timeoutId_0 = globalThis.setTimeout(() => {
        runBestEffortSync(syncUndiscoveredWindow);
      }, 300);
      return () => {
        globalThis.clearTimeout(timeoutId_0);
      };
    };
    $[15] = currentSessionState.currentDrop;
    $[16] = currentSessionState.hasDiscoveryError;
    $[17] = currentSessionState.isExhausted;
    $[18] = currentSessionState.isRestartingRound;
    $[19] = currentSessionState.recentlyHandledDropIds.length;
    $[20] = enabled;
    $[21] = pendingDropIdsRef;
    $[22] = syncUndiscoveredWindow;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== currentSessionState.currentDrop || $[25] !== currentSessionState.hasDiscoveryError || $[26] !== currentSessionState.isExhausted || $[27] !== currentSessionState.isLoading || $[28] !== currentSessionState.isRestartingRound || $[29] !== currentSessionState.recentlyHandledDropIds.length || $[30] !== enabled || $[31] !== pendingDropIdsRef || $[32] !== syncUndiscoveredWindow) {
    t4 = [currentSessionState.currentDrop, currentSessionState.hasDiscoveryError, currentSessionState.isExhausted, currentSessionState.isLoading, currentSessionState.isRestartingRound, currentSessionState.recentlyHandledDropIds.length, enabled, pendingDropIdsRef, syncUndiscoveredWindow];
    $[24] = currentSessionState.currentDrop;
    $[25] = currentSessionState.hasDiscoveryError;
    $[26] = currentSessionState.isExhausted;
    $[27] = currentSessionState.isLoading;
    $[28] = currentSessionState.isRestartingRound;
    $[29] = currentSessionState.recentlyHandledDropIds.length;
    $[30] = enabled;
    $[31] = pendingDropIdsRef;
    $[32] = syncUndiscoveredWindow;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[34] !== currentSessionState.isRestartingRound || $[35] !== enabled || $[36] !== setCurrentSessionState || $[37] !== stopActiveSync) {
    t5 = () => {
      if (!enabled || !currentSessionState.isRestartingRound) {
        return;
      }
      const timeoutId_1 = globalThis.setTimeout(() => {
        stopActiveSync();
        setCurrentSessionState(_temp);
      }, QUICK_VOTE_RESTART_TIMEOUT_MS);
      return () => {
        globalThis.clearTimeout(timeoutId_1);
      };
    };
    t6 = [currentSessionState.isRestartingRound, enabled, setCurrentSessionState, stopActiveSync];
    $[34] = currentSessionState.isRestartingRound;
    $[35] = enabled;
    $[36] = setCurrentSessionState;
    $[37] = stopActiveSync;
    $[38] = t5;
    $[39] = t6;
  } else {
    t5 = $[38];
    t6 = $[39];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[40] !== stopActiveSync) {
    t7 = () => () => {
      stopActiveSync();
    };
    t8 = [stopActiveSync];
    $[40] = stopActiveSync;
    $[41] = t7;
    $[42] = t8;
  } else {
    t7 = $[41];
    t8 = $[42];
  }
  useEffect(t7, t8);
};
const useMemesQuickVoteSessionState = t0 => {
  const $ = _c(31);
  const {
    contextProfile,
    enabled,
    isQuickVoteEnabled,
    isSettingsLoaded,
    pendingDropIdsRef,
    stateKey,
    waveId
  } = t0;
  const {
    currentSessionState,
    setCurrentSessionState
  } = useKeyedMemesQuickVoteSessionStore(stateKey);
  let t1;
  if ($[0] !== contextProfile || $[1] !== enabled || $[2] !== isQuickVoteEnabled || $[3] !== isSettingsLoaded || $[4] !== pendingDropIdsRef || $[5] !== setCurrentSessionState || $[6] !== waveId) {
    t1 = {
      contextProfile,
      enabled,
      isQuickVoteEnabled,
      isSettingsLoaded,
      pendingDropIdsRef,
      setCurrentSessionState,
      waveId
    };
    $[0] = contextProfile;
    $[1] = enabled;
    $[2] = isQuickVoteEnabled;
    $[3] = isSettingsLoaded;
    $[4] = pendingDropIdsRef;
    $[5] = setCurrentSessionState;
    $[6] = waveId;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const {
    stopActiveSync,
    syncUndiscoveredWindow
  } = useMemesQuickVoteWindowSync(t1);
  let t2;
  if ($[8] !== contextProfile || $[9] !== currentSessionState || $[10] !== enabled || $[11] !== isQuickVoteEnabled || $[12] !== pendingDropIdsRef || $[13] !== setCurrentSessionState || $[14] !== stateKey || $[15] !== stopActiveSync || $[16] !== syncUndiscoveredWindow || $[17] !== waveId) {
    t2 = {
      contextProfile,
      currentSessionState,
      enabled,
      isQuickVoteEnabled,
      pendingDropIdsRef,
      setCurrentSessionState,
      stateKey,
      stopActiveSync,
      syncUndiscoveredWindow,
      waveId
    };
    $[8] = contextProfile;
    $[9] = currentSessionState;
    $[10] = enabled;
    $[11] = isQuickVoteEnabled;
    $[12] = pendingDropIdsRef;
    $[13] = setCurrentSessionState;
    $[14] = stateKey;
    $[15] = stopActiveSync;
    $[16] = syncUndiscoveredWindow;
    $[17] = waveId;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  useMemesQuickVoteSessionEffects(t2);
  let t3;
  if ($[19] !== pendingDropIdsRef || $[20] !== setCurrentSessionState || $[21] !== syncUndiscoveredWindow) {
    t3 = drop => {
      setCurrentSessionState(current => applyOptimisticAdvanceState({
        current,
        dropId: drop.id,
        pendingDropIds: pendingDropIdsRef.current
      }));
      runBestEffortSync(syncUndiscoveredWindow);
    };
    $[19] = pendingDropIdsRef;
    $[20] = setCurrentSessionState;
    $[21] = syncUndiscoveredWindow;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const advanceVisibleDrop = t3;
  let t4;
  if ($[23] !== setCurrentSessionState || $[24] !== syncUndiscoveredWindow) {
    t4 = drop_0 => {
      setCurrentSessionState(current_0 => applyFailedDropRestoreState({
        current: current_0,
        failedDrop: drop_0
      }));
      runBestEffortSync(syncUndiscoveredWindow);
    };
    $[23] = setCurrentSessionState;
    $[24] = syncUndiscoveredWindow;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  const restoreFailedDrop = t4;
  let t5;
  if ($[26] !== advanceVisibleDrop || $[27] !== currentSessionState || $[28] !== restoreFailedDrop || $[29] !== syncUndiscoveredWindow) {
    t5 = {
      advanceVisibleDrop,
      restoreFailedDrop,
      sessionState: currentSessionState,
      syncUndiscoveredWindow
    };
    $[26] = advanceVisibleDrop;
    $[27] = currentSessionState;
    $[28] = restoreFailedDrop;
    $[29] = syncUndiscoveredWindow;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  return t5;
};
const useInvalidateQuickVoteUndiscoveredDrop = t0 => {
  const $ = _c(5);
  const {
    contextProfile,
    proxyId,
    queryClient,
    waveId
  } = t0;
  let t1;
  if ($[0] !== contextProfile || $[1] !== proxyId || $[2] !== queryClient || $[3] !== waveId) {
    t1 = () => {
      queryClient.invalidateQueries({
        predicate: query => {
          if (!Array.isArray(query.queryKey) || query.queryKey[0] !== MEMES_QUICK_VOTE_UNDISCOVERED_DROP_QUERY_KEY || typeof query.queryKey[1] !== "object" || query.queryKey[1] === null) {
            return false;
          }
          const undiscoveredQuery = query.queryKey[1] as {
            readonly context_profile?: string | null;
            readonly proxyId?: string | null;
            readonly waveId?: string | null;
          };
          return undiscoveredQuery.context_profile === (contextProfile ?? null) && undiscoveredQuery.proxyId === (proxyId ?? null) && undiscoveredQuery.waveId === waveId;
        }
      }).catch(_temp2);
    };
    $[0] = contextProfile;
    $[1] = proxyId;
    $[2] = queryClient;
    $[3] = waveId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const useMemesQuickVoteQueueSubmission = t0 => {
  const $ = _c(21);
  const {
    invalidateQuickVoteUndiscoveredDrop,
    requestAuth,
    session,
    setAndPersistRecentAmounts,
    setOptimisticRemainingVotePowerState,
    setToast,
    stateKey
  } = t0;
  let t1;
  if ($[0] !== invalidateQuickVoteUndiscoveredDrop || $[1] !== session || $[2] !== setOptimisticRemainingVotePowerState || $[3] !== stateKey) {
    t1 = (drop, amount, type) => {
      session.restoreFailedDrop(drop);
      if (type === "vote") {
        setOptimisticRemainingVotePowerState(current => restoreOptimisticRemainingVotePower({
          amount,
          current: getCurrentOptimisticRemainingVotePowerState({
            key: stateKey,
            state: current
          }),
          remainingPower: getQuickVoteAbsoluteRemainingPower(getQuickVoteRatingRange(drop))
        }));
      }
      invalidateQuickVoteUndiscoveredDrop();
    };
    $[0] = invalidateQuickVoteUndiscoveredDrop;
    $[1] = session;
    $[2] = setOptimisticRemainingVotePowerState;
    $[3] = stateKey;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== session || $[6] !== setOptimisticRemainingVotePowerState || $[7] !== stateKey) {
    t2 = (drop_0, amount_0, type_0) => {
      session.advanceVisibleDrop(drop_0);
      if (type_0 === "vote") {
        setOptimisticRemainingVotePowerState(current_0 => reduceOptimisticRemainingVotePower({
          amount: amount_0,
          current: getCurrentOptimisticRemainingVotePowerState({
            key: stateKey,
            state: current_0
          }),
          remainingPower: getQuickVoteAbsoluteRemainingPower(getQuickVoteRatingRange(drop_0))
        }));
      }
    };
    $[5] = session;
    $[6] = setOptimisticRemainingVotePowerState;
    $[7] = stateKey;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== invalidateQuickVoteUndiscoveredDrop || $[10] !== session || $[11] !== setOptimisticRemainingVotePowerState || $[12] !== stateKey) {
    t3 = (_drop, _amount, nextRemainingPower, type_1) => {
      if (type_1 === "vote") {
        setOptimisticRemainingVotePowerState(current_1 => reconcileOptimisticRemainingVotePower({
          current: getCurrentOptimisticRemainingVotePowerState({
            key: stateKey,
            state: current_1
          }),
          nextRemainingPower
        }));
      }
      invalidateQuickVoteUndiscoveredDrop();
      runBestEffortSync(session.syncUndiscoveredWindow);
    };
    $[9] = invalidateQuickVoteUndiscoveredDrop;
    $[10] = session;
    $[11] = setOptimisticRemainingVotePowerState;
    $[12] = stateKey;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== requestAuth || $[15] !== setAndPersistRecentAmounts || $[16] !== setToast || $[17] !== t1 || $[18] !== t2 || $[19] !== t3) {
    t4 = {
      onRatingFailure: t1,
      onRatingQueued: t2,
      onRatingSuccess: t3,
      requestAuth,
      setAndPersistRecentAmounts,
      setToast
    };
    $[14] = requestAuth;
    $[15] = setAndPersistRecentAmounts;
    $[16] = setToast;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  return useMemesQuickVoteSubmit(t4);
};
export const useMemesQuickVoteQueue = t0 => {
  const $ = _c(67);
  const {
    enabled: t1,
    sessionId
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    requestAuth,
    setToast
  } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const {
    contextProfile,
    isEnabled: isQuickVoteEnabled,
    isLoaded: isSettingsLoaded,
    memesWaveId,
    proxyId
  } = useMemesQuickVoteContext();
  const waveId = typeof memesWaveId === "string" && memesWaveId.length > 0 ? memesWaveId : null;
  const stateKey = `${sessionId}:${waveId ?? ""}:${contextProfile ?? ""}`;
  let t2;
  if ($[0] !== stateKey) {
    t2 = () => createInitialOptimisticRemainingVotePowerState(stateKey);
    $[0] = stateKey;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [optimisticRemainingVotePowerState, setOptimisticRemainingVotePowerState] = useState(t2);
  let t3;
  if ($[2] !== contextProfile || $[3] !== memesWaveId) {
    t3 = {
      contextProfile,
      memesWaveId
    };
    $[2] = contextProfile;
    $[3] = memesWaveId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    recentAmountsByRecency,
    setAndPersistRecentAmounts
  } = useMemesQuickVoteStorage(t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const pendingDropIdsRef = useRef(t4);
  let t5;
  if ($[6] !== contextProfile || $[7] !== enabled || $[8] !== isQuickVoteEnabled || $[9] !== isSettingsLoaded || $[10] !== stateKey || $[11] !== waveId) {
    t5 = {
      contextProfile,
      enabled,
      isQuickVoteEnabled,
      isSettingsLoaded,
      pendingDropIdsRef,
      stateKey,
      waveId
    };
    $[6] = contextProfile;
    $[7] = enabled;
    $[8] = isQuickVoteEnabled;
    $[9] = isSettingsLoaded;
    $[10] = stateKey;
    $[11] = waveId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const session = useMemesQuickVoteSessionState(t5);
  let t6;
  if ($[13] !== contextProfile || $[14] !== proxyId || $[15] !== queryClient || $[16] !== waveId) {
    t6 = {
      contextProfile,
      proxyId,
      queryClient,
      waveId
    };
    $[13] = contextProfile;
    $[14] = proxyId;
    $[15] = queryClient;
    $[16] = waveId;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const invalidateQuickVoteUndiscoveredDrop = useInvalidateQuickVoteUndiscoveredDrop(t6);
  let t7;
  if ($[18] !== invalidateQuickVoteUndiscoveredDrop || $[19] !== requestAuth || $[20] !== session || $[21] !== setAndPersistRecentAmounts || $[22] !== setToast || $[23] !== stateKey) {
    t7 = {
      invalidateQuickVoteUndiscoveredDrop,
      requestAuth,
      session,
      setAndPersistRecentAmounts,
      setOptimisticRemainingVotePowerState,
      setToast,
      stateKey
    };
    $[18] = invalidateQuickVoteUndiscoveredDrop;
    $[19] = requestAuth;
    $[20] = session;
    $[21] = setAndPersistRecentAmounts;
    $[22] = setToast;
    $[23] = stateKey;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const {
    pendingDropIds,
    submitSkip,
    submitVote
  } = useMemesQuickVoteQueueSubmission(t7);
  let t8;
  let t9;
  if ($[25] !== pendingDropIds) {
    t8 = () => {
      pendingDropIdsRef.current = pendingDropIds;
    };
    t9 = [pendingDropIds];
    $[25] = pendingDropIds;
    $[26] = t8;
    $[27] = t9;
  } else {
    t8 = $[26];
    t9 = $[27];
  }
  useEffect(t8, t9);
  const activeApiDrop = session.sessionState.currentDrop;
  const nextApiDrop = session.sessionState.lookaheadDrops[0] ?? null;
  let t10;
  if ($[28] !== activeApiDrop || $[29] !== optimisticRemainingVotePowerState || $[30] !== stateKey) {
    t10 = getEffectiveOptimisticRemainingVotePower({
      activeApiDrop,
      state: getCurrentOptimisticRemainingVotePowerState({
        key: stateKey,
        state: optimisticRemainingVotePowerState
      })
    });
    $[28] = activeApiDrop;
    $[29] = optimisticRemainingVotePowerState;
    $[30] = stateKey;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const effectiveOptimisticRemainingPower = t10;
  let t11;
  bb0: {
    if (!activeApiDrop) {
      t11 = null;
      break bb0;
    }
    let t12;
    if ($[32] !== activeApiDrop || $[33] !== effectiveOptimisticRemainingPower) {
      t12 = clampDropRatingRange(convertApiDropToExtendedDrop(activeApiDrop), effectiveOptimisticRemainingPower);
      $[32] = activeApiDrop;
      $[33] = effectiveOptimisticRemainingPower;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t11 = t12;
  }
  const activeDropCandidate = t11;
  let t12;
  if ($[35] !== activeDropCandidate) {
    t12 = isMemesQuickVoteVoteableDrop(activeDropCandidate) ? activeDropCandidate : null;
    $[35] = activeDropCandidate;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const activeDrop = t12;
  let t13;
  bb1: {
    if (!nextApiDrop) {
      t13 = null;
      break bb1;
    }
    let t14;
    if ($[37] !== effectiveOptimisticRemainingPower || $[38] !== nextApiDrop) {
      t14 = clampDropRatingRange(convertApiDropToExtendedDrop(nextApiDrop), effectiveOptimisticRemainingPower);
      $[37] = effectiveOptimisticRemainingPower;
      $[38] = nextApiDrop;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    t13 = t14;
  }
  const nextDropCandidate = t13;
  let t14;
  if ($[40] !== nextDropCandidate) {
    t14 = isMemesQuickVoteVoteableDrop(nextDropCandidate) ? nextDropCandidate : null;
    $[40] = nextDropCandidate;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const nextDrop = t14;
  let t15;
  if ($[42] !== recentAmountsByRecency) {
    t15 = recentAmountsByRecency.at(-1) ?? null;
    $[42] = recentAmountsByRecency;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const latestUsedAmount = t15;
  let t16;
  if ($[44] !== recentAmountsByRecency) {
    t16 = getDisplayQuickVoteAmounts(recentAmountsByRecency);
    $[44] = recentAmountsByRecency;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const recentAmounts = t16;
  const t17 = session.sessionState.isLoading && activeDrop === null;
  const t18 = activeDrop !== null;
  let t19;
  if ($[46] !== session.syncUndiscoveredWindow) {
    t19 = () => {
      runBestEffortSync(session.syncUndiscoveredWindow);
    };
    $[46] = session.syncUndiscoveredWindow;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== activeDrop) {
    t20 = activeDrop ? getQuickVoteAbsoluteRemainingPower(getQuickVoteRatingRange(activeDrop)) : null;
    $[48] = activeDrop;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const t21 = activeDrop ? WAVE_VOTING_LABELS[activeDrop.wave.voting_credit_type] : null;
  let t22;
  if ($[50] !== activeDrop || $[51] !== latestUsedAmount || $[52] !== nextDrop || $[53] !== recentAmounts || $[54] !== session.sessionState.hasDiscoveryError || $[55] !== session.sessionState.isExhausted || $[56] !== session.sessionState.isRestartingRound || $[57] !== session.sessionState.leftThisRoundCount || $[58] !== session.sessionState.unratedCount || $[59] !== submitSkip || $[60] !== submitVote || $[61] !== t17 || $[62] !== t18 || $[63] !== t19 || $[64] !== t20 || $[65] !== t21) {
    t22 = {
      activeDrop,
      hasDiscoveryError: session.sessionState.hasDiscoveryError,
      isExhausted: session.sessionState.isExhausted,
      isLoading: t17,
      isRestartingRound: session.sessionState.isRestartingRound,
      isReady: t18,
      leftThisRoundCount: session.sessionState.leftThisRoundCount,
      latestUsedAmount,
      nextDrop,
      recentAmounts,
      retryDiscovery: t19,
      skipDrop: submitSkip,
      submitVote,
      uncastPower: t20,
      unratedCount: session.sessionState.unratedCount,
      votingLabel: t21
    };
    $[50] = activeDrop;
    $[51] = latestUsedAmount;
    $[52] = nextDrop;
    $[53] = recentAmounts;
    $[54] = session.sessionState.hasDiscoveryError;
    $[55] = session.sessionState.isExhausted;
    $[56] = session.sessionState.isRestartingRound;
    $[57] = session.sessionState.leftThisRoundCount;
    $[58] = session.sessionState.unratedCount;
    $[59] = submitSkip;
    $[60] = submitVote;
    $[61] = t17;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  return t22;
};
function _temp(current) {
  if (!current.isRestartingRound) {
    return current;
  }
  return {
    ...current,
    hasDiscoveryError: true,
    isLoading: false,
    isRestartingRound: false
  };
}
function _temp2() {}